import { Link, useParams } from "react-router-dom";
import { Card, Grid, Space, Spin, Statistic, Tag, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import CountUp from "react-countup";
import { valueType } from "antd/lib/statistic/utils";
import { useHash } from "hooks";
import { useGetUserStatsQuery } from "api";
import { defaultQueryStringify, getRouteByName } from "libs";

const formatter = (value: valueType) => (
  <CountUp end={Number(value)} separator="," />
);

/** Format dwell seconds the same way the mobile owner dashboard does. */
const formatDwell = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

export const UserStatisticPage = () => {
  const { userId } = useParams();

  useHash("statistics");

  const { data, isLoading } = useGetUserStatsQuery({ id: userId });

  const breakpoints = Grid.useBreakpoint();

  // Trend indicator copy + color — mirrors the mobile WoW chip.
  const trendDelta = data?.profileWeekOverWeekDelta ?? 0;
  const trendPct = Math.round(Math.abs(trendDelta) * 100);
  const trendUp = trendDelta > 0;
  const trendDown = trendDelta < 0;

  return (
    <Spin spinning={isLoading}>
      <Space
        direction={breakpoints.xs ? "vertical" : "horizontal"}
        wrap
        size={[16, 16]}
      >
        <Card>
          <Link
            to={{
              pathname: getRouteByName("UserPage", { userId }),
              search: defaultQueryStringify({ "filters[type][$eq]": 1 }),
              hash: "#posts",
            }}
          >
            <Statistic
              title="Free posts"
              value={data?.freePosts}
              formatter={formatter}
            />
          </Link>
        </Card>
        <Card>
          <Link
            to={{
              pathname: getRouteByName("UserPage", { userId }),
              search: defaultQueryStringify({ "filters[type][$eq]": 2 }),
              hash: "#posts",
            }}
          >
            <Statistic
              title="Paid Posts"
              value={data?.paidPosts}
              formatter={formatter}
            />
          </Link>
        </Card>
        <Card>
          <Link
            to={{
              pathname: getRouteByName("UserPage", { userId }),
              hash: "#transactions",
            }}
          >
            <Statistic
              title="Transactions"
              value={data?.transactions}
              formatter={formatter}
            />
          </Link>
        </Card>
        <Card>
          <Link
            to={{
              pathname: getRouteByName("UserPage", { userId }),
              hash: "#reports",
            }}
          >
            <Statistic
              title="Reports"
              value={data?.reports}
              formatter={formatter}
            />
          </Link>
        </Card>
      </Space>

      {/* Profile analytics block (feature #9 mirror for admin). Shown
          below the engagement counters because it tells a different
          story — visibility/popularity vs creator output. Range
          locked to 30 days because that's the windows the owner
          sees on mobile; matching here keeps the conversation
          1-to-1 when admin contacts the user about their stats. */}
      <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
        Statistici profil — ultimele 30 de zile
      </Typography.Title>
      <Space
        direction={breakpoints.xs ? "vertical" : "horizontal"}
        wrap
        size={[16, 16]}
      >
        <Card>
          <Statistic
            title="Vizite profil"
            value={data?.profileVisits30d ?? 0}
            formatter={formatter}
            prefix={<EyeOutlined />}
          />
        </Card>
        <Card>
          <Statistic
            title="Vizitatori unici"
            value={data?.profileUniqueVisitors30d ?? 0}
            formatter={formatter}
            prefix={<TeamOutlined />}
          />
        </Card>
        <Card>
          <Statistic
            title="Timp mediu pe profil"
            value={formatDwell(data?.profileAvgDwellSeconds30d ?? 0)}
            prefix={<FieldTimeOutlined />}
          />
        </Card>
        <Card>
          <Statistic
            title="Tendință (vs săpt. anterioară)"
            valueRender={() => (
              <Tag
                color={trendUp ? "green" : trendDown ? "red" : "default"}
                icon={
                  trendUp ? (
                    <ArrowUpOutlined />
                  ) : trendDown ? (
                    <ArrowDownOutlined />
                  ) : null
                }
                style={{ fontSize: 16, padding: "4px 10px" }}
              >
                {trendUp || trendDown ? `${trendPct}%` : "—"}
              </Tag>
            )}
          />
        </Card>
      </Space>
    </Spin>
  );
};
